package com.eladfinish.bible.contest.screens.browse.contentOfChapter.ui

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.pager.PagerState
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.material3.SnackbarDuration
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.lifecycle.Lifecycle
import androidx.lifecycle.compose.LocalLifecycleOwner
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.bookmark_added
import com.eladfinish.bible.contest.navigation.NavigationCommand
import com.eladfinish.bible.contest.platform.clipboard.ObserveClipboardTextWhileStarted
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.BrowseChaptersAction
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.BrowseChaptersEffect
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.BrowseChaptersState.PartTabSpec
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.BrowseChaptersState.PartUiState
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.BrowseChaptersStore
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.BrowseChaptersStore.Companion.BrowseChaptersStoreParams
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.ui.dialogs.BrowseChaptersDialogs
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.BrowseMode
import com.eladfinish.bible.contest.screens.browse.tableOfContents.ui.ExposureTypesRow
import com.eladfinish.bible.contest.ui.common.SetScreenIsLoading
import com.eladfinish.bible.contest.ui.components.toolbar.UpdateToolbarConfiguration
import com.eladfinish.bible.contest.ui.components.toolbar.autohide.LocalToolbarAutoHideController
import com.eladfinish.bible.contest.ui.components.toolbar.autohide.rememberToolbarAutoHideController
import com.eladfinish.bible.contest.ui.components.toolbar.autohide.toolbarAutoHide
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarHeaderComponentConfig
import com.eladfinish.bible.contest.ui.extensions.LocalNavigationBarInset
import com.eladfinish.bible.contest.ui.extensions.navigationBarInsetPadding
import com.eladfinish.bible.contest.ui.menus.viewModels.ToolbarActionBusViewModel
import dev.icerock.moko.resources.StringResource
import dev.icerock.moko.resources.desc.StringDesc
import kotlinx.collections.immutable.ImmutableList
import kotlinx.collections.immutable.ImmutableMap
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

@Composable
fun ChapterTabsWithHorizontalPager(
    browseMode: BrowseMode,
    isCompleteChapter: Boolean,
    bookIndex: Int,
    partIndex: Int,
    bookmarkScrollY: Int,
    bookmarkTextOffset: Int = -1,
    completionDetails: String?,
    showOnqelos: Boolean,
    verse: Int = 0,
    showToast: (String) -> Unit,
    showToastStringResource: (StringResource) -> Unit,
    showSnackbar: (message: StringDesc, actionLabel: StringDesc?, duration: SnackbarDuration, onActionPerformed: () -> Unit) -> Unit,
    onBookmarkScrollYChanged: (y: Int, textOffset: Int) -> Unit,
    navigator: (NavigationCommand.Navigate) -> Unit,
    toolbarActionBus: ToolbarActionBusViewModel,
    browseChaptersStore: BrowseChaptersStore = koinViewModel(
        parameters = {
            parametersOf(
                BrowseChaptersStoreParams(
                    browseMode = browseMode,
                    isCompleteChapter = isCompleteChapter,
                    bookIndex = bookIndex,
                    partIndex = partIndex,
                    bookmarkScrollY = bookmarkScrollY,
                    bookmarkTextOffset = bookmarkTextOffset,
                    completionDetails = completionDetails,
                    showOnqelos = showOnqelos,
                )
            )
        }
    ),
) {
    val state by browseChaptersStore.observeState().collectAsState()

    val goToMarkerController = rememberGoToMarkerController()

    // Opened at a verse (the rotating verse widget's link): scrolls to it and highlights it for a moment,
    // as the aliyah buttons do for an aliyah. Not before the screen is in front: until the transition ends it is
    // still under the loader of the screen it replaced, and the highlight would fade out unseen.
    val lifecycleOwner = LocalLifecycleOwner.current
    // Saved: after a rotation the reader stays where they scrolled, instead of jumping back to the verse.
    var wentToVerse by rememberSaveable(verse) { mutableStateOf(false) }
    LaunchedEffect(verse) {
        if (verse <= 0 || wentToVerse) return@LaunchedEffect
        lifecycleOwner.lifecycle.currentStateFlow.first { it == Lifecycle.State.RESUMED }
        wentToVerse = true
        goToMarkerController.goToVerse(verse)
    }

    ObserveClipboardTextWhileStarted { copiedText ->
        browseChaptersStore.dispatch(BrowseChaptersAction.ClipboardTextCopied(copiedText))
    }

    ChapterContentToolbarBindings(
        onAction = browseChaptersStore::dispatch,
        toolbarActionBus = toolbarActionBus,
    )

    ChapterHeaderTitle(
        title = state.bookName,
    )

    val pagerState = rememberBookScopedPagerState(
        currentBook = state.currentBook,
        tabsCount = state.tabs.size,
        initialChapterPage = state.currentPart,
    )

    ObserveStorePagerSideEffects(
        effects = browseChaptersStore.observeSideEffect(),
        goToAliyah = {
            goToMarkerController.goToAliyah(which = it)
        },
        scrollToTextOffset = {
            goToMarkerController.scrollToTextOffset(offset = it)
        },
        scrollToPosition = {
            goToMarkerController.scrollToY(yOffset = it)
        },
        notifyBookmarkSaved = {
            showToastStringResource(SharedRes.strings.bookmark_added)
        },
        showToast = {
            showToast(it)
        },
        showToastStringResource = {
            showToastStringResource(it)
        },
        navigator = navigator,
    )

    SyncPagerWithStore(
        pagerState = pagerState,
        currentBook = state.currentBook,
        currentChapterPage = state.currentPart,
    )

    val toolbarAutoHideController = rememberToolbarAutoHideController(pagerState)

    CompositionLocalProvider(LocalToolbarAutoHideController provides toolbarAutoHideController) {
        ChapterTabsScreen(
            isCompleteChapter = state.isCompleteChapter,
            isDebugMode = state.isDebugMode,
            pagerState = pagerState,
            tabs = state.tabs,
            chapterState = state.parts,
            previousBookName = state.previousBookName,
            nextBookName = state.nextBookName,
            textSize = state.textSize,
            onAction = { browseChaptersStore.dispatch(it) },
            goToMarkerController = goToMarkerController,
            onBookmarkScrollYChanged = onBookmarkScrollYChanged,
            currentBook = state.currentBook,
            deceasedNamePage = state.deceasedNamePage,
            modifier = Modifier.toolbarAutoHide(toolbarAutoHideController),
        )
    }

    BrowseChaptersDialogs(
        dialogState = state.dialogState,
        onAction = { browseChaptersStore.dispatch(it) },
    )

    SetScreenIsLoading(state.isLoading)
}

@Composable
private fun ChapterHeaderTitle(title: String) {
    UpdateToolbarConfiguration(
        update = { copy(title = ToolbarHeaderComponentConfig.Text(text = title)) },
        title,
    )
}

@Composable
private fun rememberBookScopedPagerState(
    currentBook: Int,
    tabsCount: Int,
    initialChapterPage: Int,
): PagerState {
    return key(currentBook) {
        rememberPagerState(
            pageCount = { tabsCount },
            initialPage = initialChapterPage,
        )
    }
}

@Composable
private fun ObserveStorePagerSideEffects(
    effects: Flow<BrowseChaptersEffect>,
    goToAliyah: (String) -> Unit,
    scrollToPosition: (Int) -> Unit,
    scrollToTextOffset: (Int) -> Unit,
    notifyBookmarkSaved: () -> Unit,
    showToast: (String) -> Unit,
    showToastStringResource: (StringResource) -> Unit,
    navigator: (NavigationCommand.Navigate) -> Unit,
) {
    LaunchedEffect(Unit) {
        effects.collect { effect ->
            when (effect) {
                is BrowseChaptersEffect.ScrollToAliyah -> {
                    goToAliyah(effect.aliyahLabel)
                }

                is BrowseChaptersEffect.ScrollToPosition -> {
                    if (effect.textOffset >= 0) {
                        scrollToTextOffset(effect.textOffset)
                    } else {
                        scrollToPosition(effect.yOffest)
                    }
                }

                BrowseChaptersEffect.NotifyBookmarkSaved -> {
                    notifyBookmarkSaved()
                }

                is BrowseChaptersEffect.ShowToast -> {
                    showToast(effect.text)
                }

                is BrowseChaptersEffect.ShowToastStringResource -> {
                    showToastStringResource(effect.resource)
                }

                is BrowseChaptersEffect.OpenContent -> {
                    navigator(effect.navigation)
                }
            }
        }
    }
}

@Composable
private fun SyncPagerWithStore(
    pagerState: PagerState,
    currentBook: Int,
    currentChapterPage: Int,
) {
    LaunchedEffect(currentBook, currentChapterPage) {
        if (pagerState.currentPage != currentChapterPage) {
            pagerState.scrollToPage(currentChapterPage)
        }
    }
}

@Composable
internal fun ChapterTabsScreen(
    isCompleteChapter: Boolean,
    isDebugMode: Boolean,
    pagerState: PagerState,
    tabs: ImmutableList<PartTabSpec>,
    chapterState: ImmutableMap<Int, PartUiState>,
    previousBookName: String,
    nextBookName: String,
    textSize: Float,
    currentBook: Int,
    goToMarkerController: GoToMarkerController,
    onBookmarkScrollYChanged: (y: Int, textOffset: Int) -> Unit,
    onAction: (BrowseChaptersAction) -> Unit,
    deceasedNamePage: Int? = null,
    modifier: Modifier = Modifier,
) {
    // The "שונות" tab has no books: no arrows, nor pages, to the previous and the next one.
    val hasBookEdges = tabs.firstOrNull() == PartTabSpec.ArrowRight

    ObservePagerSettledNavigation(
        pagerState = pagerState,
        hasBookEdges = hasBookEdges,
        onNavigateToPreviousBook = { onAction(BrowseChaptersAction.NavigateToPreviousBook) },
        onNavigateToNextBook = { onAction(BrowseChaptersAction.NavigateToNextBook) },
        onChapterSettled = { chapterIndex1Based ->
            onAction(
                BrowseChaptersAction.PartSettled(
                    bookIndex = currentBook,
                    partIndex = chapterIndex1Based
                )
            )
        }
    )

    val selectedTabIndex by rememberSelectedTabIndex(pagerState)
    val scope = rememberCoroutineScope()

    Column(modifier.fillMaxSize()) {
        ChapterTabsRow(
            selectedTabIndex = selectedTabIndex,
            tabs = tabs,
            onTabClick = { tabIndex, tabSpec ->
                onActionForTabClick(
                    scope = scope,
                    pagerState = pagerState,
                    tabIndex = tabIndex,
                    tabSpec = tabSpec,
                    onNavigateToPreviousBook = { onAction(BrowseChaptersAction.NavigateToPreviousBook) },
                    onNavigateToNextBook = { onAction(BrowseChaptersAction.NavigateToNextBook) },
                )
            }
        )

        // In Complete Chapter the buttons row below takes the navigation bar; the text ends above it.
        CompositionLocalProvider(
            LocalNavigationBarInset provides if (isCompleteChapter) 0.dp else LocalNavigationBarInset.current,
        ) {
            ChapterPager(
                pagerState = pagerState,
                hasBookEdges = hasBookEdges,
                previousBookName = previousBookName,
                nextBookName = nextBookName,
                chapterState = chapterState,
                textSize = textSize,
                goToMarkerController = goToMarkerController,
                onBookmarkScrollYChanged = onBookmarkScrollYChanged,
                onAction = onAction,
                deceasedNamePage = deceasedNamePage,
                onChapterVisible = { chapterIndex1Based ->
                    onAction(
                        BrowseChaptersAction.PageVisible(
                            bookIndex = currentBook,
                            partIndex = chapterIndex1Based,
                        )
                    )
                },
            )
        }

        if (isCompleteChapter) {
            ExposureTypesRow(
                modifier = Modifier
                    .navigationBarInsetPadding()
                    .padding(horizontal = 8.dp),
                onTypeClick = {
                    onAction(BrowseChaptersAction.ExposureTypesClicked(completeType = it))
                }
            )
        }
    }
}

internal fun onActionForTabClick(
    scope: CoroutineScope,
    pagerState: PagerState,
    tabIndex: Int,
    tabSpec: PartTabSpec,
    onNavigateToPreviousBook: () -> Unit,
    onNavigateToNextBook: () -> Unit,
) {
    when (tabSpec) {
        PartTabSpec.ArrowRight -> onNavigateToPreviousBook()
        PartTabSpec.ArrowLeft -> onNavigateToNextBook()
        is PartTabSpec.Part -> scope.launch { pagerState.animateScrollToPage(tabIndex) }
    }
}
